import { useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";

import { endOfDay, startOfMonth, subMonths } from "date-fns";
import { RefreshCwIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useListLogsNotificationLogsGetQuery } from "api";
import { IZone } from "interfaces/zone.interface";
import { toIsoString } from "utils/date.util";
import { mapById } from "utils/mapById";

import TimeStampFilter from "./components/TimeStampFilter";
import { NotificationLogList } from "./NotificationLogList";

const pageSize = 20;

type NotificationLogListProps = {
  entityId: string;
  zones: IZone[];
};

export function NotificationLogsScreen({
  entityId,
  zones,
}: NotificationLogListProps) {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState({
    start: subMonths(startOfMonth(new Date()), 3),
    end: endOfDay(new Date()),
    type: "custom",
  });

  const [pageStack, setPageStack] = useState<string[]>([]);

  const {
    data: logsData,
    isLoading,
    isFetching,
    refetch,
  } = useListLogsNotificationLogsGetQuery({
    entityId: entityId,
    limit: pageSize,
    from: toIsoString(dateRange.start),
    to: toIsoString(dateRange.end),
    startingAfter: null,
    endingBefore: pageStack.at(-1) ?? null,
    sort: "desc",
  });

  const logs = logsData?.data ?? [];

  return (
    <Box sx={{ borderRadius: 1, minHeight: "500px", width: "100%" }}>
      <Stack
        direction={"row"}
        sx={{
          padding: 1,
        }}
      >
        <IconButton onClick={refetch}>
          <RefreshCwIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <TimeStampFilter
          value={dateRange}
          onChange={(values) => {
            setPageStack([]);
            setDateRange(values);
          }}
        />
      </Stack>
      {logs.length === 0 && (
        <Typography sx={{ paddingBlock: 2 }}>
          {isLoading ? "..." : t("there_are_no_logs_to_display")}
        </Typography>
      )}
      {logs.length > 0 && (
        <NotificationLogList logs={logs} zoneEntity={mapById(zones)} />
      )}
      <Stack
        direction={"row"}
        sx={{ gap: 1, paddingTop: 1, justifyContent: "flex-end" }}
      >
        <Button
          onClick={() => setPageStack((s) => s.slice(0, -1))}
          disabled={pageStack.length === 0}
          variant={"contained"}
          loading={isFetching}
        >
          {t("previous")}
        </Button>
        <Button
          onClick={() => setPageStack((s) => [...s, logs.at(-1)?.id ?? ""])}
          disabled={logs.length < pageSize}
          variant={"contained"}
          loading={isFetching}
        >
          {t("next")}
        </Button>
      </Stack>
    </Box>
  );
}
